import { filter } from "lodash";
import React, { useState } from "react";
import { capitalCase } from "change-case";
// material
import { useTheme } from "@mui/material/styles";
import {
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  Link,
  Paper} from "@mui/material";
// components
import SearchNotFound from "./SearchNotFound";
import ProjectsTableHead from "./ProjectsTableHead";
import ProjectsToolbar from "./ProjectsToolbar";
import Label from "../util/Label";
import { fNumber } from "../util/formatNumber";
// import Scrollbar from '../../Scrollbar';
// ----------------------------------------------------------------------

// name, department, subcounty, ward, status, FY

const TABLE_HEAD = [
  { id: "name", label: "Project Name", alignRight: false },
  { id: "department", label: "Department", alignRight: false },
  { id: "subcounty", label: "Sub County", alignRight: false },
  { id: "ward", label: "Ward", alignRight: false },
  { id: "amount", label: "Amount in KES", alignRight: false },
  { id: "status", label: "Status", alignRight: false },
  { id: "cidp", label: "CIDP", alignRight: false },
  { id: "contractor", label: "Contractor", alignRight: false },
  { id: "fy", label: "Year", alignRight: false },
];

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(
      array,
      (_project) =>
        _project.name.toLowerCase().indexOf(query.toLowerCase()) !== -1
    );
  }
  return stabilizedThis.map((el) => el[0]);
}

export default function CountyProjectsTable({
  countyProjects,
  numberOfProjects,
}) {
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState("asc");
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState("name");
  const [filterName, setFilterName] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const theme = useTheme();

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = countyProjects.map((n) => n.name);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const filteredProjects = applySortFilter(
    countyProjects,
    getComparator(order, orderBy),
    filterName
  );

  const isProjectNotFound = filteredProjects.length === 0;

  return (
    <>
      {/* <Scrollbar> */}
      <ProjectsToolbar
        numSelected={selected.length}
        filterName={filterName}
        onFilterName={handleFilterByName}
      ></ProjectsToolbar>
      <TableContainer component={Paper} sx={{ minWidth: 800, maxHeight: 450 }}>
        <Table className="data-table" stickyHeader>
          <ProjectsTableHead
            order={order}
            orderBy={orderBy}
            headLabel={TABLE_HEAD}
            rowCount={countyProjects.length}
            numSelected={selected.length}
            onRequestSort={handleRequestSort}
            onSelectAllClick={handleSelectAllClick}
          />
          <TableBody>
            {filteredProjects
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row) => {
                const {
                  id,
                  name,
                  department,
                  constituency,
                  ward,
                  status,
                  cidp,
                  year,
                  contractor,
                  amount,
                } = row;
                const isItemSelected = selected.indexOf(name) !== -1;
                let labelColor = "warning";
                if (status && status === "Not started") {
                  labelColor = "error";
                } else if (status && status === "Complete") {
                  labelColor = "success";
                } else if (status && status === "Complete & operational") {
                  labelColor = "info";
                } else if (status && status === "Incomplete") {
                  labelColor = "warning";
                }

                return (
                  <TableRow
                    hover
                    key={id}
                    tabIndex={-1}
                    role="checkbox"
                    selected={isItemSelected}
                    aria-checked={isItemSelected}
                  >
                    <TableCell align="left">
                      <Link href={`/projects/${id}`}>{capitalCase(name)}</Link>
                    </TableCell>
                    <TableCell align="left">
                      {department.name ? capitalCase(department.name) : "-"}
                    </TableCell>
                    <TableCell align="left">
                      {constituency.name ? constituency.name : "-"}
                    </TableCell>
                    <TableCell align="left">
                      {ward.name ? ward.name : "-"}
                    </TableCell>
                    <TableCell align="left">
                      {amount !== 0 ? fNumber(amount) : "-"}
                    </TableCell>
                    <TableCell align="left">
                      <Label
                        variant={
                          theme.palette.mode === "light" ? "ghost" : "filled"
                        }
                        color={labelColor}
                      >
                        {capitalCase(status ? status : "Unavailable")}
                      </Label>
                    </TableCell>
                    <TableCell align="left">{cidp ? cidp : "-"}</TableCell>
                    <TableCell align="left">
                      {contractor ? contractor : "-"}
                    </TableCell>
                    <TableCell align="left">{year}</TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
          {isProjectNotFound && (
            <TableBody>
              <TableRow>
                <TableCell align="center" colSpan={6}>
                  <SearchNotFound searchQuery={filterName} />
                </TableCell>
              </TableRow>
            </TableBody>
          )}
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={numberOfProjects}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      {/* </Scrollbar> */}
    </>
  );
}
